import React, { useState, useContext } from "react";
import AlertContext from "../../../../Context/Alert/AlertContext";
import { BaseURL } from "../../../../Files/BaseURL";
import { useNavigate } from "react-router-dom";

const ConnectCard = ({ setConnect }) => {
  const { showAlert } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    holderName: "",
    cardNumber: "",
    cardExpiry: "",
    cardCVC: "",
  });
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});

  // Add formatted states for display
  const [formattedCardNumber, setFormattedCardNumber] = useState("");
  const [formattedExpiry, setFormattedExpiry] = useState("");

  const formatCardNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");
    // Limit to 16 digits
    const truncated = cleaned.slice(0, 16);
    // Add spaces every 4 digits
    const formatted = truncated.replace(/(\d{4})(?=\d)/g, "$1 ");

    setFormattedCardNumber(formatted);
    // Store raw number in state
    setCardDetails((prev) => ({
      ...prev,
      cardNumber: truncated,
    }));
  };

  const formatExpiryDate = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");
    // Limit to 4 digits
    const truncated = cleaned.slice(0, 4);

    let formatted = truncated;
    // Add slash after first 2 digits if length > 2
    if (truncated.length > 2) {
      formatted = `${truncated.slice(0, 2)}/${truncated.slice(2)}`;
    }

    setFormattedExpiry(formatted);
    // Store raw date in state
    setCardDetails((prev) => ({
      ...prev,
      cardExpiry: formatted,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "cardNumber") {
      formatCardNumber(value);
    } else if (name === "cardExpiry") {
      formatExpiryDate(value);
    } else if (name === "cardCVC") {
      // Only allow numbers and max 4 digits for CVC
      const cleaned = value.replace(/\D/g, "").slice(0, 4);
      setCardDetails((prev) => ({
        ...prev,
        [name]: cleaned,
      }));
    } else {
      setCardDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle paste events
  const handlePaste = (e) => {
    const { name } = e.target;
    const pastedText = e.clipboardData.getData("text");

    e.preventDefault();

    if (name === "cardNumber") {
      formatCardNumber(pastedText);
    } else if (name === "cardExpiry") {
      formatExpiryDate(pastedText);
    }
  };

  const validate = () => {
    let validationErrors = {};
    const cardNumberPattern = /^\d{16}$/;
    const cvcPattern = /^\d{3,4}$/;
    const expiryPattern = /^(0[1-9]|1[0-2])\/\d{2}$/;

    if (!cardDetails.holderName.trim()) {
      validationErrors.holderName = "Card holder name is required";
    }
    if (!cardNumberPattern.test(cardDetails.cardNumber)) {
      validationErrors.cardNumber = "Card number must be 16 digits";
    }
    if (!expiryPattern.test(cardDetails.cardExpiry)) {
      validationErrors.cardExpiry = "Expiry date must be in MM/YY format";
    }
    if (!cvcPattern.test(cardDetails.cardCVC)) {
      validationErrors.cardCVC = "CVC must be 3 or 4 digits";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showAlert("Please correct the errors before submitting", "error");
      return;
    }
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem("p@token");
      const response = await fetch(
        `${BaseURL}/api/payment/account/attachcard`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": authToken,
          },
          body: JSON.stringify(cardDetails),
        }
      );

      const data = await response.json();

      if (!data.success) {
        showAlert(data.message || "Failed to connect card", "error");
        setIsLoading(false);
        return;
      }

      //Setting up token
      const setupToken = await fetch(
        `${BaseURL}/api/payment/account/setup-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": authToken,
          },
          body: JSON.stringify({ type: "card" }),
        }
      );
      let setupTokenID = "";
      if (setupToken.ok) {
        const response = await setupToken.json();
        setupTokenID = response.id;
      } else {
        showAlert("Failed to create vault setup token", "error");
        setIsLoading(false);
        return;
      }

      // Payment token
      const paymentToken = await fetch(
        `${BaseURL}/api/payment/account/payment-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": authToken,
          },
          body: JSON.stringify({ type: "card", vaultSetupToken: setupTokenID }),
        }
      );

      if (paymentToken.ok) {
        showAlert("Card connected successfully!", "success");
        setConnect(false);
        navigate("/signin");
        setIsLoading(false);
      } else {
        showAlert("Failed to approve vault payment token", "error");
        setIsLoading(false);
        return;
      }
    } catch (error) {
      showAlert("Internal Server Error", "error");
    }
  };

  return (
    <>
      <div className="font-pop relative z-10 py-20">
        <div className="mx-6 bg-white max-w-md w-full p-4 md:p-8 rounded-lg shadow-md">
          <h2 className="text-lg md:text-2xl font-pop font-semibold mb-4">
            Connect your card
          </h2>
          <p className="text-gray-600 mb-6 text-sm md:text-base max-w-sm">
            We Respect Your Privacy and don't worry everything is safe with us
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col font-pop gap-4"
          >
            <input
              type="text"
              id="holderName"
              name="holderName"
              placeholder="Card Holder Name"
              value={cardDetails.holderName}
              onChange={handleChange}
              className="w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />
            {errors.holderName && (
              <p className="text-red-500 text-sm">{errors.holderName}</p>
            )}

            <input
              type="text"
              id="cardNumber"
              name="cardNumber"
              placeholder="Card Number"
              value={formattedCardNumber}
              onChange={handleChange}
              onPaste={handlePaste}
              maxLength={19} // 16 digits + 3 spaces
              className="w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />
            {errors.cardNumber && (
              <p className="text-red-500 text-sm">{errors.cardNumber}</p>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                id="cardExpiry"
                name="cardExpiry"
                placeholder="MM/YY"
                value={formattedExpiry}
                onChange={handleChange}
                onPaste={handlePaste}
                maxLength={5} // MM/YY format
                className="w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none basis-[75%]"
              />
              <input
                type="text"
                id="cardCVC"
                name="cardCVC"
                placeholder="CVC"
                value={cardDetails.cardCVC}
                onChange={handleChange}
                maxLength={4}
                className="w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none basis-[25%]"
              />
            </div>
            {errors.cardExpiry && (
              <p className="text-red-500 text-sm">{errors.cardExpiry}</p>
            )}
            {errors.cardCVC && (
              <p className="text-red-500 text-sm">{errors.cardCVC}</p>
            )}

            <button
              type="submit"
              className="font-pop text-white text-lg bg-primarygreen rounded-3xl py-2 hover:bg-transparent border-2 border-primarygreen ease-in-out duration-300 hover:text-primarygreen disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                </div>
              ) : (
                "Connect Card"
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ConnectCard;
